/**
 * Layout route for any /dashboards/<key>/* URL. Renders the shared
 * DashboardShell (heading + tab strip) and the active tab via Outlet.
 * Specific tab routes (e.g. _app.dashboards.schedule.dispatch.tsx)
 * become children of this layout automatically.
 */

import { createFileRoute, Outlet, notFound, redirect } from '@tanstack/react-router';
import { DashboardShell } from '~/components/dashboard/DashboardShell';
import { DASHBOARDS } from '~/lib/dashboards';
import { useAuth } from '~/lib/auth';
import { canAccess } from '~/lib/access';
import { Card, CardBody, CardHeader } from '~/components/ui';
import { RouteErrorComponent } from '~/components/ErrorState';

/**
 * Sections that no longer exist, and where their contents went. Keeps old
 * bookmarks and links working instead of 404-ing. The old tab segment is
 * dropped — you land on the new section's first tab.
 */
const MOVED_DASHBOARDS: Record<string, string> = {
  schedule: 'operations',              // Schedule folded back into Operations (9/23)
  'insurance-adjusting': 'collections', // the whole section folded into Collections
};

export const Route = createFileRoute('/_app/dashboards/$dashboard')({
  beforeLoad: ({ params }) => {
    const moved = MOVED_DASHBOARDS[params.dashboard];
    if (moved) {
      throw redirect({ to: '/dashboards/$dashboard', params: { dashboard: moved }, replace: true });
    }
    if (!DASHBOARDS[params.dashboard]) throw notFound();
  },
  component: DashboardLayout,
  errorComponent: RouteErrorComponent,
});

function DashboardLayout() {
  const { dashboard: key } = Route.useParams();
  const { profile, loading } = useAuth();
  const dashboard = DASHBOARDS[key];
  if (!dashboard) return null;

  const roles = (profile?.staff_role_keys as string[] | null) ?? [];
  if (!loading && profile && !canAccess(`/dashboards/${key}`, roles)) {
    return (
      <div className="px-6 py-6 max-w-7xl mx-auto w-full">
        <Card>
          <CardHeader>No access</CardHeader>
          <CardBody>
            <div className="text-sm text-[var(--color-ink-muted)]">
              Your role doesn't have access to the {dashboard.label} dashboard. Contact an admin if you need it.
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <DashboardShell dashboard={dashboard}>
      <Outlet />
    </DashboardShell>
  );
}
